<script lang="ts">
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import type { ImportResponseType } from '../../shared/ColumnSelectPro/currentImport';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import { ImportApi } from './ImportApi';
    import { api, clientSettings, customerSettings } from '../../../stores/api';
    import GeocodingStatus from './GeocodingStatus.svelte';
    import { debug } from '../../../stores/store';
    import { Import } from './Import';
    import { ExportType } from '../Export/ExportType';
    import { ExportApi } from '../Export/ExportApi';
    import { captureMessage } from '@sentry/svelte';
    import IconDownload from '../../shared/Icons/IconDownload.svelte';
    import IconPending from '../../shared/Icons/IconPending.svelte';
    import { features } from '../../shared/Features/features';
    import { ExportTypes } from '../Export/ExportTypes';
    import IconError from '../../shared/Icons/IconError.svelte';
    import { Umami } from '../../utilities/Umami';

    export let _import: ImportResponseType;
    export let showHeader: boolean = false;
    export let error: string = '';
    export let onUpdateOne: () => void;

    export let downloadingInProgress: boolean;

    let localDownloadingInProgress: boolean = false;

    let __import: Import;

    let et: ExportType = new ExportType('realisation_by_import', 'realisation_by_import');

    function removeOne() {
        error = '';
        let confirm = window.confirm($_('Czy na pewno chcesz usunąć ten import?'));
        if (!confirm) return;
        if (_import) {
            ImportApi.remove(get(api), __import.getId())
                .then(() => {
                    Umami.event('import_delete', { import_id: __import.getId() });
                    _import = null;
                    onUpdateOne();
                })
                .catch(e => {
                    Umami.error('import_delete', e, { import_id: __import.getId() });
                    error = $_('delete_file_failed'); // TODO: Co ma zrobić user?
                });
        }
    }

    async function download(et: ExportType, import_id: number) {
        downloadingInProgress = true;
        localDownloadingInProgress = true;
        // https://www.programonaut.com/how-to-create-a-download-for-a-zip-file-with-sveltekit-step-by-step/
        let exportFile = await ExportApi.getFile($api, et.getApiEndpoint(), {
            type: et.getType().long,
            import_id: import_id,
            sort_by_zone: $customerSettings.export?.realisation_sort_by_zone ?? 'by_zone',
        }).catch(error => {
            console.error('Error downloading file', error);
            alert($_('error_downloading_file'));
            captureMessage('Error downloading file', {
                extra: {
                    error,
                },
            });
        });

        if (!exportFile) {
            downloadingInProgress = false;
            localDownloadingInProgress = false;
            return;
        }

        // convert exportFile (raw csv file) to blob
        let blob = new Blob([exportFile], { type: et.getType().long });

        let url = window.URL || window.webkitURL;
        let link = url.createObjectURL(blob);

        // generate anchor tag, click it for download and then remove it again
        let a = document.createElement('a');
        a.setAttribute('download', et.getOutputFileName());
        a.setAttribute('href', link);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // revoke the object URL to free up memory
        url.revokeObjectURL(link);
        Umami.event('import_export_realisation', { import_id: import_id });
        downloadingInProgress = false;
        localDownloadingInProgress = false;
    }

    __import = new Import(_import);
</script>

{#if showHeader}
    <div class="list-element import-element list-element-header">
        <div class="uploaded">{$_('Uploaded')}</div>
        <div class="filename">{$_('File name')}</div>
        <div class="status">{$_('Status')}</div>
        <div class="remove"></div>
    </div>
{/if}

{#if _import}
    <div class="list-element import-element list-element-data">
        <div class="uploaded">{(_import?.uploaded && _import.uploaded) || '---'}</div>
        <div class="filename">
            {#if $debug}
                <span>#{_import?.id}&nbsp;</span>
            {/if}
            {(_import?.filename && _import.filename) || '---'}
        </div>
        <div class="status">
            {#if _import.error !== null}
                <span><IconError scale={0.7} title={$_('import_error_occurred')} /></span>
            {:else if __import.isCompleted()}
                <span>OK</span>
            {:else if __import.inProgress()}
                <GeocodingStatus inProgress="Geocoding" onDone="OK" bind:_import />
            {:else}
                <span>---</span>
            {/if}

        </div>
        <div class="remove">
            {#if localDownloadingInProgress}
                <IconPending scale={0.7} spin={true} />
            {:else}
                <IconDownload
                    scale={0.7}
                    disabled={downloadingInProgress}
                    on:click={async () => {
                        if (!_import) {
                            console.error('No import');
                            return;
                        }
                        const filenameWithoutExtension = _import.filename?.replace(/\.[^/.]+$/, '') || 'export';
                        et.setOutputFileName(filenameWithoutExtension + '.processed.' + _import.temporary_file_extension);
                        console.log('export', et.getApiEndpoint());
                        if (_import.filename) {
                            let _file_extension = _import.filename.split('.').pop();
                            _file_extension = _file_extension?.toLowerCase() ?? undefined;
                            if (_file_extension === 'xlsx') {
                                et.setType(ExportTypes.XLSX);
                            } else if (_file_extension === 'csv') {
                                et.setType(ExportTypes.CSV);
                            } else if (_file_extension === 'ods') {
                                et.setType(ExportTypes.ODS);
                            } else {
                                et.setType(ExportTypes.XLSX);
                            }
                        }
                        await download(et, _import.id);
                    }}
                />
            {/if}
            <IconRemove scale={0.7} on:click={removeOne} />
        </div>
    </div>
{/if}

<style>
    .import-element {
        grid-template-columns: 150px 1fr 90px 70px;
        grid-template-rows: var(--medium-row-height);
        grid-template-areas: 'uploaded filename status remove';
    }

    .import-element.list-element-header > div,
    .import-element.list-element-data > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        /*justify-content: center;*/
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .import-element .filename {
        grid-area: filename;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .import-element .status {
        grid-area: status;
        justify-content: center;
    }

    .import-element .remove {
        grid-area: remove;
        justify-content: center;
        column-gap: 6px;
    }

    .import-element .uploaded {
        grid-area: uploaded;
        justify-content: center;
    }
</style>
